import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, NgZone } from '@angular/core';
import { isOsDirectoryFileDrag } from '@swissfex/sfx-commons';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import {
    CANCELLED_REQUEST_ERROR,
    DEFAULT_PAGE_THUMBNAIL_WIDTH,
    DefaultEmptyLenderId,
    DOCUMENT_UPLOAD_FORM_DATA_KEYS,
    SafeAny,
    SPECIFIC_DOCUMENT_GROUPS,
    UPLOAD_STATUSES
} from '../../constants';
import { ROUTE_ID } from '../../constants/api-routes';
import { TDocumentIdKey } from '../../constants/types';
import { HttpService } from '../../core/services/http.service';
import { DocumentUtils } from '../../core/utils/document.utils';
import { DocumentIdKey } from '../edit-project/constants';
import { DossierFileStatus } from '../models/enums/dossierFileStatus';
import { DossierProcessingStatus } from '../models/enums/dossierProcessingStatus';
import { IAssignDocument } from '../models/IAssignDocument';
import { IBaseDocument } from '../models/IBaseDocument';
import { IDocument, IDossierDocument } from '../models/IDocument';
import { IDocumentCoverImage } from '../models/IDocumentCoverImage';
import { IDocumentCoverImages } from '../models/IDocumentCoverImages';
import { IDocumentDownloadRequestId } from '../models/IDocumentDownloadRequestId';
import { IDocumentDownloadRequestStatus } from '../models/IDocumentDownloadRequestStatus';
import { IDocumentGenerationParams } from '../models/IDocumentGenerationParams';
import { IDocumentGroupCompleteStatus } from '../models/IDocumentGroupCompleteStatus';
import { IDocumentPageThumbnailBatch } from '../models/IDocumentPageThumbnailBatch';
import { IDocumentToExtract } from '../models/IDocumentToExtract';
import { IDossierFileStatus } from '../models/IDossierFileStatus';
import { IDossierStatus } from '../models/IDossierStatus';
import { IGeneratedDocument } from '../models/IGeneratedDocument';
import { IDossierInboxQueueItem, IInboxQueueItem } from '../models/IInboxQueueItem';
import { IRequiredDocuments } from '../models/IRequiredDocuments';
import { IWrappedBoolean } from '../models/IWrappedBoolean';

interface IDocumentUploadInProgress {
    documentId: string;
}

interface IDocumentDrag {
    inProgress: boolean;
    type?: 'internal' | 'os-directory';
}

interface IDeletedDocument {
    id: number | string;
    isHypoDossierDocument: boolean;
    documentGroup: string;
}

type DocumentAssignedToGroup = IAssignDocument & { isUnassignedFromNotCategorizedGroup?: boolean };

@Injectable({
    providedIn: 'root'
})
export class DocumentsService {
    private readonly document = inject(DOCUMENT);
    private readonly httpService = inject(HttpService);
    private readonly ngZone = inject(NgZone);

    public currentPreviewedDocumentThumbnailsObservable: Observable<IDocumentPageThumbnailBatch>;
    public previewedDocumentObservable: Observable<IDocument>;
    public documentForVersionsObservable: Observable<IDocument>;
    public isPreviewLoadingObservable: Observable<boolean>;
    public requiredDocumentsObservable: Observable<IRequiredDocuments>;
    public openAssignModalObservable: Observable<IDocument>;
    public openSplitModalObservable: Observable<void>;
    public documentAssignedToGroupObservable: Observable<DocumentAssignedToGroup>;
    public makeDocumentGroupVisibleObservable: Observable<IAssignDocument>;
    public dossierStatusObservable: Observable<IDossierStatus>;
    public signalGetDossierStatusObservable: Observable<void>;
    public didGetDossierStatusFailObservable: Observable<HttpErrorResponse>;
    public displayOrRemoveDossierStatusNotificationObservable: Observable<boolean>;
    public isDossierProcessingInProgressObservable: Observable<boolean>;
    public calculateDocumentsFooterActionObservable: Observable<void>;
    public hypoDossierDocumentUploadsInProgressObservable: Observable<IDocumentUploadInProgress[]>;
    public pendingRequiredDocumentsRequestsCountObservable: Observable<number>;
    public hasPendingRequiredDocumentsRequestsObservable: Observable<boolean>;
    public signalGetRequiredDocumentsObservable: Observable<void>;
    public didGetRequiredDocumentsFailObservable: Observable<HttpErrorResponse>;
    public isDocumentDragInProgressObservable: Observable<IDocumentDrag>;
    public documentDeletedObservable: Observable<IDeletedDocument>;
    public isRedesignedDocumentsPageUsedObservable: Observable<boolean>;

    private _inboxQueueObservable: Observable<IInboxQueueItem[]>;
    private requiredDocumentsSubject: BehaviorSubject<IRequiredDocuments>;
    private inboxQueueSubject: BehaviorSubject<IInboxQueueItem[]>;
    private currentPreviewedDocumentThumbnailsSubject: BehaviorSubject<IDocumentPageThumbnailBatch>;
    private isPreviewLoadingSubject: BehaviorSubject<boolean>;
    private previewedDocumentSubject: BehaviorSubject<IDocument>;
    private documentForVersionsSubject: BehaviorSubject<IDocument>;
    private openAssignModalSubject: Subject<IDocument>;
    private openSplitModalSubject: Subject<void>;
    private documentAssignedToGroupSubject: Subject<DocumentAssignedToGroup>;
    private makeDocumentGroupVisibleSubject: Subject<IAssignDocument>;
    private dossierStatusSubject: BehaviorSubject<IDossierStatus>;
    private signalGetDossierStatusSubject: Subject<void>;
    private didGetDossierStatusFailSubject: Subject<HttpErrorResponse>;
    private signalDisplayOrRemoveDossierStatusNotificationSubject: Subject<boolean>;
    private isDossierStatusNotificationClosedByUser: BehaviorSubject<boolean>;
    private isDossierStatusPollingInProgress: BehaviorSubject<boolean>;
    private isDossierProcessingInProgressSubject: BehaviorSubject<boolean>;
    private calculateDocumentsFooterActionSubject: Subject<void>;
    private hypoDossierDocumentUploadsInProgressSubject: BehaviorSubject<IDocumentUploadInProgress[]>;
    private pendingRequiredDocumentsRequestsCountSubject: BehaviorSubject<number>;
    private signalGetRequiredDocumentsSubject: Subject<void>;
    private didGetRequiredDocumentsFailSubject: Subject<HttpErrorResponse>;
    private isDocumentDragInProgressSubject: Subject<IDocumentDrag>;
    private documentDeletedSubject: Subject<IDeletedDocument>;
    private isRedesignedDocumentsPageUsedSubject: BehaviorSubject<boolean>;
    private dossierStatusPollingInterval: ReturnType<typeof setInterval>;
    private getRequiredDocsRequestCount = 0;

    private currentIsDocumentDragInProgress = false;
    private osDirectoryFileDragListenerRefCount = 0;
    private osDirectoryFileDragCleanup: () => void = null;
    private _osDirectoryFileDragOverTimeout: ReturnType<typeof setTimeout>;
    private readonly OS_DIRECTORY_FILE_DRAG_IDLE_MS = 150;

    // eslint-disable-next-line @typescript-eslint/related-getter-setter-pairs
    private get osDirectoryFileDragOverTimeout(): ReturnType<typeof setTimeout> {
        return this._osDirectoryFileDragOverTimeout;
    }

    private set osDirectoryFileDragOverTimeout(delegate: () => ReturnType<typeof setTimeout>) {
        clearTimeout(this._osDirectoryFileDragOverTimeout);
        this._osDirectoryFileDragOverTimeout = delegate();
    }

    /**
     * Contains the `documentId`s for which the thumbnails have been generated ({@link thumbnailsGeneratedNotificationSubject} has been emitted),
     * but the actual document is still not part of the {@link requiredDocumentsSubject}.
     */
    private missingDocumentIdsWithGeneratedThumbnails: number[] = [];

    constructor() {
        this.inboxQueueSubject = new BehaviorSubject<IInboxQueueItem[]>([]);

        this.currentPreviewedDocumentThumbnailsSubject = new BehaviorSubject<IDocumentPageThumbnailBatch>(undefined);
        this.currentPreviewedDocumentThumbnailsObservable =
            this.currentPreviewedDocumentThumbnailsSubject.asObservable();

        this.isPreviewLoadingSubject = new BehaviorSubject<boolean>(false);
        this.isPreviewLoadingObservable = this.isPreviewLoadingSubject.asObservable();

        this.requiredDocumentsSubject = new BehaviorSubject<IRequiredDocuments>(undefined);
        this.requiredDocumentsObservable = this.requiredDocumentsSubject.asObservable();

        this.previewedDocumentSubject = new BehaviorSubject<IDocument>(undefined);
        this.previewedDocumentObservable = this.previewedDocumentSubject.asObservable();

        this.documentForVersionsSubject = new BehaviorSubject<IDocument>(undefined);
        this.documentForVersionsObservable = this.documentForVersionsSubject.asObservable();

        this.openAssignModalSubject = new Subject<IDocument>();
        this.openAssignModalObservable = this.openAssignModalSubject.asObservable();

        this.openSplitModalSubject = new Subject<void>();
        this.openSplitModalObservable = this.openSplitModalSubject.asObservable();

        this.documentAssignedToGroupSubject = new Subject<DocumentAssignedToGroup>();
        this.documentAssignedToGroupObservable = this.documentAssignedToGroupSubject.asObservable();

        this.makeDocumentGroupVisibleSubject = new BehaviorSubject<IAssignDocument>(undefined);
        this.makeDocumentGroupVisibleObservable = this.makeDocumentGroupVisibleSubject.asObservable();

        this.dossierStatusSubject = new BehaviorSubject<IDossierStatus>(undefined);
        this.dossierStatusObservable = this.dossierStatusSubject.asObservable();

        this.signalGetDossierStatusSubject = new Subject<void>();
        this.signalGetDossierStatusObservable = this.signalGetDossierStatusSubject.asObservable();

        this.didGetDossierStatusFailSubject = new Subject<HttpErrorResponse>();
        this.didGetDossierStatusFailObservable = this.didGetDossierStatusFailSubject.asObservable();

        this.signalDisplayOrRemoveDossierStatusNotificationSubject = new Subject<boolean>();
        this.displayOrRemoveDossierStatusNotificationObservable =
            this.signalDisplayOrRemoveDossierStatusNotificationSubject.asObservable();

        this.isDossierStatusNotificationClosedByUser = new BehaviorSubject<boolean>(false);

        this.isDossierStatusPollingInProgress = new BehaviorSubject<boolean>(false);

        this.isDossierProcessingInProgressSubject = new BehaviorSubject<boolean>(false);
        this.isDossierProcessingInProgressObservable = this.isDossierProcessingInProgressSubject.asObservable();

        this.calculateDocumentsFooterActionSubject = new Subject<void>();
        this.calculateDocumentsFooterActionObservable = this.calculateDocumentsFooterActionSubject.asObservable();

        this.hypoDossierDocumentUploadsInProgressSubject = new BehaviorSubject<IDocumentUploadInProgress[]>(undefined);
        this.hypoDossierDocumentUploadsInProgressObservable =
            this.hypoDossierDocumentUploadsInProgressSubject.asObservable();

        this.pendingRequiredDocumentsRequestsCountSubject = new BehaviorSubject<number>(
            this.getRequiredDocsRequestCount
        );
        this.pendingRequiredDocumentsRequestsCountObservable =
            this.pendingRequiredDocumentsRequestsCountSubject.asObservable();

        this.hasPendingRequiredDocumentsRequestsObservable = this.pendingRequiredDocumentsRequestsCountObservable.pipe(
            map((count) => count > 0)
        );

        this.signalGetRequiredDocumentsSubject = new Subject<void>();
        this.signalGetRequiredDocumentsObservable = this.signalGetRequiredDocumentsSubject.asObservable();

        this.didGetRequiredDocumentsFailSubject = new Subject<HttpErrorResponse>();
        this.didGetRequiredDocumentsFailObservable = this.didGetRequiredDocumentsFailSubject.asObservable();

        this.isDocumentDragInProgressSubject = new Subject<IDocumentDrag>();
        this.isDocumentDragInProgressObservable = this.isDocumentDragInProgressSubject.asObservable();

        this.documentDeletedSubject = new Subject<IDeletedDocument>();
        this.documentDeletedObservable = this.documentDeletedSubject.asObservable();

        this.isRedesignedDocumentsPageUsedSubject = new BehaviorSubject<boolean>(
            environment.features.documents.useRedesignedDocumentsPage
        );
        this.isRedesignedDocumentsPageUsedObservable = this.isRedesignedDocumentsPageUsedSubject.asObservable();
    }

    public get inboxQueueObservable(): Observable<IInboxQueueItem[]> {
        if (!this._inboxQueueObservable) {
            this._inboxQueueObservable = this.inboxQueueSubject.asObservable();
        }

        return this._inboxQueueObservable;
    }

    public clearCache(): void {
        this.inboxQueueSubject.next([]);
        this.cancelRequestsAndRemoveDossierStatusNotification();
        this.setDossierStatus(undefined);
        this.hypoDossierDocumentUploadsInProgressSubject.next(undefined);
        this.clearRequiredDocumentsCache();
        this.clearPreviewedDocumentCache();
        this.clearVisibleDocumentGroupCache();
    }

    public cancelRequestsAndRemoveDossierStatusNotification(): void {
        this.stopDossierStatusPolling();
        this.cancelGetDossierStatusRequest();
        this.cancelDocumentUploads(this.getHypoDossierDocumentUploadsInProgress());
        this.signalRemoveDossierStatusNotification();
    }

    public clearPreviewedDocumentCache(): void {
        this.currentPreviewedDocumentThumbnailsSubject.next(undefined);
        this.isPreviewLoadingSubject.next(false);
        this.setPreviewedDocument(undefined);
    }

    public clearRequiredDocumentsCache(): void {
        this.requiredDocumentsSubject.next(undefined);
    }

    public clearVisibleDocumentGroupCache(): void {
        this.makeDocumentGroupVisibleSubject.next(undefined);
    }

    public triggerDocumentsFooterActionCalculation(): void {
        this.calculateDocumentsFooterActionSubject.next();
    }

    public async downloadDocumentBlob(projectId: number, id: number): Promise<Blob> {
        return await this.httpService
            .get<Blob>(null, ROUTE_ID.DOCUMENT_BY_ID, { projectId, id }, { responseType: 'blob' })
            .catch((errorResponse: unknown) => this.handleBlobErrorResponse(errorResponse as HttpErrorResponse));
    }

    public async downloadOfferDocumentBlob(projectId: number, documentId: number): Promise<Blob> {
        return await this.httpService
            .get<Blob>(null, ROUTE_ID.OFFER_DOCUMENT_BY_ID, { projectId, documentId }, { responseType: 'blob' })
            .catch((errorResponse: unknown) => this.handleBlobErrorResponse(errorResponse as HttpErrorResponse));
    }

    public async downloadDocumentsByOfferId(projectId: number, id: number): Promise<IBaseDocument> {
        return await this.httpService.get<IBaseDocument>(null, ROUTE_ID.DOCUMENTS_BY_OFFER_ID, { projectId, id });
    }

    public async getDocumentDownloadRequestId(projectId: number): Promise<string> {
        const { requestId } = await this.httpService.post<IDocumentDownloadRequestId>(
            ROUTE_ID.DOCUMENT_DOWNLOAD_REQUEST_ID,
            {},
            { projectId },
            undefined,
            undefined,
            {}
        );
        return requestId;
    }

    public cancelGetDocumentDownloadRequestId() {
        this.httpService.cancelRequest(ROUTE_ID.DOCUMENT_DOWNLOAD_REQUEST_ID, 'POST');
    }

    public async getDocumentDownloadRequestStatus(
        projectId: number,
        requestId: string
    ): Promise<IDocumentDownloadRequestStatus> {
        return await this.httpService.get<IDocumentDownloadRequestStatus>(
            null,
            ROUTE_ID.DOCUMENT_DOWNLOAD_REQUEST_STATUS,
            {
                projectId,
                requestId
            },
            undefined,
            undefined,
            {}
        );
    }

    public cancelGetDocumentDownloadRequestStatus() {
        this.httpService.cancelRequest(ROUTE_ID.DOCUMENT_DOWNLOAD_REQUEST_STATUS, 'GET');
    }

    public async deleteDocument(projectId: number, id: number): Promise<void> {
        await this.httpService.delete(ROUTE_ID.DOCUMENT_BY_ID, { projectId, id });
        this.removeDocumentFromPreviewedDocumentCache(projectId, id);
        const removedDocument = this.removeDocumentFromRequiredDocumentCache(id);
        this.notifyDocumentDeleted({ id, isHypoDossierDocument: false, documentGroup: removedDocument?.documentGroup });
    }

    public async deleteHypoDossierDocument(projectId: number, hypoDossierDocumentId: string): Promise<void> {
        await this.httpService.delete(ROUTE_ID.HYPO_DOSSIER_DOCUMENT_BY_ID, { projectId, hypoDossierDocumentId });
        const removedDocument = this.removeDocumentFromRequiredDocumentCache(
            hypoDossierDocumentId,
            DocumentIdKey.HypoDossierDocumentId
        );
        this.notifyDocumentDeleted({
            id: hypoDossierDocumentId,
            isHypoDossierDocument: true,
            documentGroup: removedDocument?.documentGroup
        });
    }

    public getDocumentForVersions(): IDocument {
        return this.documentForVersionsSubject.getValue();
    }

    public getHypoDossierDocumentUploadsInProgress(): IDocumentUploadInProgress[] {
        return this.hypoDossierDocumentUploadsInProgressSubject.getValue();
    }

    public getIsDossierStatusNotificationClosedByUser(): boolean {
        return this.isDossierStatusNotificationClosedByUser.getValue();
    }

    public async getDocumentPageThumbnails(
        projectId: number,
        documentId: number,
        queryParams?: { from?: number; take?: number; width?: number }
    ): Promise<IDocumentPageThumbnailBatch> {
        if (!queryParams) {
            queryParams = {};
        }

        if (!queryParams.width) {
            queryParams.width = DEFAULT_PAGE_THUMBNAIL_WIDTH;
        }

        this.isPreviewLoadingSubject.next(true);

        const response = await this.httpService.getWithQuery<IDocumentPageThumbnailBatch>(
            ROUTE_ID.DOCUMENT_PAGE_THUMBNAILS,
            {
                projectId,
                id: documentId,
                ...queryParams
            }
        );

        this.currentPreviewedDocumentThumbnailsSubject.next(response);
        this.isPreviewLoadingSubject.next(false);

        return response;
    }

    public async getRequiredDocuments(projectId: number): Promise<IRequiredDocuments> {
        try {
            this.adjustPendingRequiredDocumentsRequestsCountBy(1);
            const response = await this.httpService.get<IRequiredDocuments>(
                null,
                ROUTE_ID.REQUIRED_DOCUMENTS,
                { projectId },
                null,
                null,
                {}
            );

            this.requiredDocumentsSubject.next(response);
            return response;
        } finally {
            this.adjustPendingRequiredDocumentsRequestsCountBy(-1);
        }
    }

    public openAssignDocumentModal(document?: IDocument): void {
        this.openAssignModalSubject.next(document);
    }

    public openSplitDocumentModal(): void {
        this.openSplitModalSubject.next();
    }

    public setDocumentForVersions(document: IDocument): void {
        this.documentForVersionsSubject.next(document);
    }

    public notifyDocumentAssignedToGroup(document: DocumentAssignedToGroup): void {
        this.documentAssignedToGroupSubject.next(document);
    }

    public notifyDocumentDeleted(deletedDocument: IDeletedDocument): void {
        this.documentDeletedSubject.next(deletedDocument);
    }

    public makeDocumentGroupVisible(document: IAssignDocument): void {
        this.makeDocumentGroupVisibleSubject.next(document);
    }

    public async uploadDocument(fileName: string, formData: FormData): Promise<IDocument> {
        const inboxQueue = this.inboxQueueSubject.getValue();
        const group = formData.get(DOCUMENT_UPLOAD_FORM_DATA_KEYS.Group);
        const lenderId = +formData.get(DOCUMENT_UPLOAD_FORM_DATA_KEYS.LenderId);
        const documentId = formData.get(DOCUMENT_UPLOAD_FORM_DATA_KEYS.DocumentId) as string;
        const isUpdate = documentId && documentId !== 'null' && documentId !== 'undefined' && documentId.length;
        const document: IInboxQueueItem = {
            id: isUpdate
                ? documentId.toString()
                : `${fileName}${inboxQueue.filter((item: IInboxQueueItem) => item.name === fileName).length.toString()}`,
            name: fileName,
            progress: 0,
            didFail: false,
            failures: null,
            group: group ? (group as string) : SPECIFIC_DOCUMENT_GROUPS.NotCategorized,
            lenderId
        };
        this.addDocumentToQueue(document);

        const requestMethod = isUpdate ? this.httpService.updateUpload : this.httpService.upload;
        const requestRouteId = isUpdate ? ROUTE_ID.UPDATE_FILE : ROUTE_ID.UPLOAD_FILE;

        return new Promise<IDocument>((resolve, reject) => {
            requestMethod(requestRouteId, document.id, {}, formData).subscribe({
                next: (data) => {
                    switch (data.status) {
                        case UPLOAD_STATUSES.PROGRESS:
                            this.updateInboxQueueProgress({
                                ...document,
                                progress: data.progress
                            } satisfies IInboxQueueItem);
                            break;
                        case UPLOAD_STATUSES.RESPONSE:
                            this.removeDocumentFromInboxQueue(document.id);
                            if (isUpdate) {
                                this.removeDocumentFromRequiredDocumentCache(+documentId);
                            }
                            this.addDocumentToRequiredDocumentsCache(data.response);
                            resolve(data.response);
                            return;
                    }
                },
                error: (error: { error: { message: string } }) => {
                    document.didFail = true;
                    try {
                        const json = JSON.parse(error.error.message) as { Message: string };
                        document.failures = json.Message;
                    } catch {
                        document.failures = error.error.message;
                    }
                    this.updateInboxQueueProgress(document);
                    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                    reject(error);
                }
            });
        });
    }

    public async uploadDossierDocument(
        fileName: string,
        formData: FormData,
        canBeCancelled = false
    ): Promise<IDossierDocument> {
        const inboxQueue = this.inboxQueueSubject.getValue();
        const group = formData.get(DOCUMENT_UPLOAD_FORM_DATA_KEYS.Group);
        const lenderId = +formData.get(DOCUMENT_UPLOAD_FORM_DATA_KEYS.LenderId);
        const size = +formData.get(DOCUMENT_UPLOAD_FORM_DATA_KEYS.Size);
        const document: IDossierInboxQueueItem = {
            id: `${fileName}${inboxQueue.filter((item: IInboxQueueItem) => item.name === fileName).length.toString()}`,
            name: fileName,
            progress: 0,
            didFail: false,
            failures: null,
            group: group ? (group as string) : SPECIFIC_DOCUMENT_GROUPS.NotCategorized,
            lenderId,
            size,
            status: DossierFileStatus.Undefined,
            uploadedDate: new Date(),
            updatedDate: new Date()
        };
        this.addDocumentToQueue(document, true);

        return new Promise<IDossierDocument>((resolve, reject) => {
            this.addHypoDossierDocumentUploadInProgress(document.id);

            this.httpService.upload(ROUTE_ID.UPLOAD_FILE, document.id, {}, formData, null, canBeCancelled).subscribe({
                next: (data) => {
                    switch (data.status) {
                        case UPLOAD_STATUSES.PROGRESS:
                            this.updateInboxQueueProgress({
                                ...document,
                                progress: data.progress
                            } satisfies IDossierInboxQueueItem);
                            break;
                        case UPLOAD_STATUSES.RESPONSE:
                            this.removeDocumentFromInboxQueue(document.id);
                            this.removeHypoDossierDocumentUploadInProgress(document.id);
                            resolve(data.response);
                            return;
                    }
                },
                error: (error: { error: { message: string } } | string) => {
                    document.didFail = true;
                    if (error !== CANCELLED_REQUEST_ERROR) {
                        const exception = error as { error: { message: string } };
                        try {
                            const json = JSON.parse(exception.error.message) as { Message: string };
                            document.failures = json.Message;
                        } catch {
                            document.failures = exception.error.message;
                        }
                    }
                    this.updateInboxQueueProgress(document);
                    this.removeHypoDossierDocumentUploadInProgress(document.id);
                    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                    reject(error);
                }
            });
        });
    }

    public cancelDocumentUpload(documentId: string): void {
        this.httpService.cancelRequest(ROUTE_ID.UPLOAD_FILE, 'POST', { documentId });
    }

    public cancelDocumentUploads(documentUploadsInProgress: IDocumentUploadInProgress[]): void {
        documentUploadsInProgress?.forEach(({ documentId }) => {
            this.cancelDocumentUpload(documentId);
        });
    }

    public getCachedRequiredDocuments(): IRequiredDocuments {
        return this.requiredDocumentsSubject.getValue();
    }

    public async setCompleteStatus(projectId: number, payload: IDocumentGroupCompleteStatus): Promise<boolean> {
        const isCompleted = await this.httpService.patch<boolean>(ROUTE_ID.DOCUMENT_GROUP_SET_COMPLETE_STATUS, null, {
            projectId,
            ...payload
        });

        const cachedRequiredDocuments = this.getCachedRequiredDocuments();

        if (cachedRequiredDocuments) {
            const targetGroupId = this.getUniqueDocumentGroupId(payload.documentGroup, payload.lenderId);

            const updatedCategories = cachedRequiredDocuments.categories.map((category) => {
                const updatedGroups = category.groups.map((group) =>
                    this.getUniqueDocumentGroupId(group.groupType, group.lenderId) === targetGroupId
                        ? { ...group, isMarkedAsComplete: isCompleted }
                        : group
                );

                // These extra category checks are done so we don't trigger a new emission if the category hasn't changed, which would cause unnecessary re-rendering in components that subscribe to this observable.
                const isCategoryChanged = updatedGroups.some((group, index) => group !== category.groups[index]);
                return isCategoryChanged ? { ...category, groups: updatedGroups } : category;
            });

            this.requiredDocumentsSubject.next({
                ...cachedRequiredDocuments,
                categories: updatedCategories
            });
        }

        return isCompleted;
    }

    public async extractDocument(projectId: number, payload: IDocumentToExtract): Promise<IDocument> {
        return await this.httpService.post<IDocument>(ROUTE_ID.EXTRACT_DOCUMENT, { projectId }, payload);
    }

    public setPreviewedDocument = (document: IDocument): void => {
        this.previewedDocumentSubject.next(document);
    };

    public async assignDocumentsToGroups(projectId: number, payload: IAssignDocument[]): Promise<IDocument[]> {
        return await this.httpService.put<IDocument[]>(ROUTE_ID.ASSIGN_DOCUMENT, { projectId }, payload);
    }

    public async offerOverviewDocumentGeneration(
        projectId: number,
        payload?: IDocumentGenerationParams
    ): Promise<IGeneratedDocument> {
        return await this.httpService.post<IGeneratedDocument>(
            ROUTE_ID.OFFER_OVERVIEW_DOCUMENT_GENERATION,
            { projectId },
            payload,
            null,
            null,
            {}
        );
    }

    public async isOverRequiredDocumentsThreshold(projectId: number): Promise<IWrappedBoolean> {
        return await this.httpService.get<IWrappedBoolean>(
            null,
            ROUTE_ID.IS_OVER_REQUIRED_DOCUMENTS_THRESHOLD,
            { projectId },
            null,
            null,
            {}
        );
    }

    public async getDocumentCoverImages(projectId: number): Promise<IDocumentCoverImages> {
        return await this.httpService.get<IDocumentCoverImages>(null, ROUTE_ID.DOCUMENT_COVER_IMAGES, { projectId });
    }

    public async uploadDocumentCoverImage(projectId: number, formData: FormData): Promise<IDocumentCoverImage> {
        return new Promise<IDocumentCoverImage>((resolve, reject) => {
            this.httpService.upload(ROUTE_ID.DOCUMENT_COVER_IMAGES, undefined, { projectId }, formData).subscribe({
                next: (data) => {
                    if (data.status === UPLOAD_STATUSES.RESPONSE) {
                        resolve(data.response);
                        return;
                    }
                },
                error: (error) => {
                    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                    reject(error);
                    return;
                }
            });
        });
    }

    public cancelOfferOverviewDocumentGeneration(): void {
        this.httpService.cancelRequest(ROUTE_ID.OFFER_OVERVIEW_DOCUMENT_GENERATION, 'POST');
    }

    public async getDossierStatus(projectId: number): Promise<IDossierStatus> {
        this.isDossierStatusPollingInProgress.next(true);

        try {
            const dossierStatus = await this.httpService.get<IDossierStatus>(
                null,
                ROUTE_ID.DOSSIER_STATUS,
                { projectId },
                null,
                null,
                {}
            );

            this.setDossierStatus(dossierStatus);

            if (this.isDossierProcessingInProgress(dossierStatus)) {
                this.startDossierStatusPolling(projectId);
            } else {
                this.stopDossierStatusPolling();
            }

            return dossierStatus;
        } catch (ex) {
            this.stopDossierStatusPolling();
            throw ex;
        }
    }

    public startDossierStatusPolling(projectId: number): void {
        if (this.dossierStatusPollingInterval) {
            return;
        }

        this.dossierStatusPollingInterval = setInterval(async () => {
            await this.getDossierStatus(projectId);
        }, 5000);
    }

    public stopDossierStatusPolling(): void {
        this.isDossierStatusPollingInProgress.next(false);

        if (this.dossierStatusPollingInterval) {
            clearInterval(this.dossierStatusPollingInterval);
            this.dossierStatusPollingInterval = undefined;
        }
    }

    public cancelGetDossierStatusRequest(): void {
        this.httpService.cancelRequest(ROUTE_ID.DOSSIER_STATUS, 'GET');
    }

    public setDossierStatus(dossierStatus: IDossierStatus): void {
        this.dossierStatusSubject.next(dossierStatus);
        this.isDossierProcessingInProgressSubject.next(this.isDossierProcessingInProgress(dossierStatus));
    }

    public getCachedDossierStatus(): IDossierStatus {
        return this.dossierStatusSubject.getValue();
    }

    public getDossierProcessingStatus(dossierStatus: IDossierStatus): DossierProcessingStatus {
        if (!dossierStatus) {
            return null;
        }

        if (this.isDossierProcessingInProgress(dossierStatus)) {
            return DossierProcessingStatus.PROCESSING;
        } else if (dossierStatus.unrecognizedDocumentsCount) {
            return DossierProcessingStatus.COMPLETED_WITH_UNRECOGNIZED_DOCUMENTS;
        } else {
            return DossierProcessingStatus.COMPLETED;
        }
    }

    public removeFailedDocumentFromInboxCache(documentQueueEntry: IInboxQueueItem): void {
        const currentQueue = this.inboxQueueSubject.getValue();
        const updatedQueue = currentQueue.filter((item) => item !== documentQueueEntry);

        if (updatedQueue.length !== currentQueue.length) {
            this.inboxQueueSubject.next(updatedQueue);
        }
    }

    public removeDocumentFromPreviewedDocumentCache(projectId: number, documentId: number): void {
        const document = this.previewedDocumentSubject.getValue();
        if (!!document && document.documentId === documentId && document.projectId === projectId) {
            this.clearPreviewedDocumentCache();
        }
    }

    public updateDocumentInRequiredDocumentsCache(
        document: IDocument,
        documentIdKey: TDocumentIdKey = DocumentIdKey.DocumentId
    ): void {
        const requiredDocuments = this.getCachedRequiredDocuments();
        if (!document || !requiredDocuments) {
            return;
        }

        const {
            document: foundDocument,
            categoryIndex,
            groupIndex
        } = this.findDocument(requiredDocuments, document[documentIdKey], documentIdKey);

        if (foundDocument) {
            const updatedDocuments = this.getUpdatedRequiredDocuments(
                requiredDocuments,
                categoryIndex,
                groupIndex,
                (documents) =>
                    documents.map((doc) => (doc[documentIdKey] === foundDocument[documentIdKey] ? document : doc))
            );

            this.requiredDocumentsSubject.next(updatedDocuments);
        }
    }

    public removeDocumentFromRequiredDocumentCache(
        documentId: number | string,
        documentIdKey: TDocumentIdKey = DocumentIdKey.DocumentId
    ): IDocument {
        const requiredDocuments = this.getCachedRequiredDocuments();
        if (!requiredDocuments) {
            return null;
        }

        const { document, categoryIndex, groupIndex } = this.findDocument(requiredDocuments, documentId, documentIdKey);

        if (document) {
            const updatedDocuments = this.getUpdatedRequiredDocuments(
                requiredDocuments,
                categoryIndex,
                groupIndex,
                (documents) => documents.filter((doc) => doc[documentIdKey] !== documentId)
            );

            this.requiredDocumentsSubject.next(updatedDocuments);
            return document;
        }

        return null;
    }

    public addDocumentToRequiredDocumentsCache(
        document: IDocument,
        previewDocument = true,
        documentIdKey: TDocumentIdKey = DocumentIdKey.DocumentId
    ): void {
        let documentToAdd = document;

        if (documentIdKey === DocumentIdKey.DocumentId) {
            const thumbnailIndex = this.missingDocumentIdsWithGeneratedThumbnails.findIndex(
                (id) => id === document.documentId
            );

            if (thumbnailIndex > -1) {
                documentToAdd = { ...document, areThumbnailsGenerated: true };
                this.missingDocumentIdsWithGeneratedThumbnails.removeAt(thumbnailIndex);
            }
        }

        const requiredDocuments = this.getCachedRequiredDocuments();
        if (!requiredDocuments) {
            return;
        }

        let isDocumentAdded = false;
        let areDocumentsChanged = false;

        const documentIdValue = document[documentIdKey];

        const updatedCategories = requiredDocuments.categories.map((category) => {
            const updatedGroups = category.groups.map((group) => {
                const hasExistingDocument = group.assignedDocuments.some(
                    (doc) => doc[documentIdKey] === documentIdValue && doc.lenderId === document.lenderId
                );
                const isTargetGroup =
                    group.groupType === document.documentGroup && group.lenderId === document.lenderId;

                if (hasExistingDocument || isTargetGroup) {
                    const newDocuments = group.assignedDocuments.filter(
                        (doc) => !(doc[documentIdKey] === documentIdValue && doc.lenderId === document.lenderId)
                    );

                    if (isTargetGroup) {
                        newDocuments.push(documentToAdd);
                        isDocumentAdded = true;
                    }

                    areDocumentsChanged = true;
                    return { ...group, assignedDocuments: newDocuments };
                }

                return group;
            });

            const isCategoryChanged = updatedGroups.some((group, index) => group !== category.groups[index]);
            return isCategoryChanged ? { ...category, groups: updatedGroups } : category;
        });

        if (areDocumentsChanged) {
            if (isDocumentAdded && documentToAdd.areThumbnailsGenerated && previewDocument) {
                this.setPreviewedDocument(documentToAdd);
            }

            const updatedRequiredDocuments = { ...requiredDocuments, categories: updatedCategories };
            this.requiredDocumentsSubject.next(updatedRequiredDocuments);
        }
    }

    public updateThumbnailsGeneratedForDocInRequiredDocumentsCache(documentId: number): void {
        const requiredDocuments = this.getCachedRequiredDocuments();
        if (!requiredDocuments) {
            return;
        }

        const { document, categoryIndex, groupIndex } = this.findDocument(requiredDocuments, documentId);

        if (document) {
            const updatedDocument = { ...document, areThumbnailsGenerated: true };
            const updatedDocuments = this.getUpdatedRequiredDocuments(
                requiredDocuments,
                categoryIndex,
                groupIndex,
                (documents) => documents.map((doc) => (doc.documentId === documentId ? updatedDocument : doc))
            );

            const previewedDocument = this.previewedDocumentSubject.getValue();
            if (previewedDocument && previewedDocument?.documentId === documentId) {
                this.setPreviewedDocument(updatedDocument);
            }

            this.requiredDocumentsSubject.next(updatedDocuments);
        } else if (!this.missingDocumentIdsWithGeneratedThumbnails.includes(documentId)) {
            this.missingDocumentIdsWithGeneratedThumbnails.push(documentId);
        }
    }

    public updateInboxQueueProgress(document: IInboxQueueItem): void {
        const currentQueue = this.inboxQueueSubject.getValue();
        const updatedQueue = currentQueue.map((item) => (item.id === document.id ? document : item));

        this.inboxQueueSubject.next(updatedQueue);
    }

    public signalDisplayDossierStatusNotificationIfDossierProcessingIsInProgress(dossierStatus: IDossierStatus): void {
        if (this.isDossierProcessingInProgress(dossierStatus)) {
            this.signalDisplayDossierStatusNotification();
        }
    }

    public signalDisplayDossierStatusNotification(): void {
        this.signalDisplayOrRemoveDossierStatusNotificationSubject.next(true);
    }

    public signalRemoveDossierStatusNotification(): void {
        this.signalDisplayOrRemoveDossierStatusNotificationSubject.next(false);
    }

    public signalIsDocumentDragInProgress(documentDrag: IDocumentDrag): void {
        if (this.currentIsDocumentDragInProgress === documentDrag.inProgress) {
            return;
        }

        this.currentIsDocumentDragInProgress = documentDrag.inProgress;
        this.isDocumentDragInProgressSubject.next(documentDrag);
    }

    public setIsDossierStatusNotificationClosedByUser(value: boolean): void {
        this.isDossierStatusNotificationClosedByUser.next(value);
    }

    public signalGetRequiredDocuments(): void {
        this.signalGetRequiredDocumentsSubject.next();
    }

    public signalGetRequiredDocumentsFailed(exception: HttpErrorResponse): void {
        this.didGetRequiredDocumentsFailSubject.next(exception);
    }

    public signalGetDossierStatus(): void {
        this.signalGetDossierStatusSubject.next();
    }

    public signalGetDossierStatusFailed(exception: HttpErrorResponse): void {
        this.didGetDossierStatusFailSubject.next(exception);
    }

    public isDossierProcessingInProgress(dossierStatus: IDossierStatus): boolean {
        return !!dossierStatus?.files?.some((file: IDossierFileStatus) => file.status === DossierFileStatus.Processing);
    }

    public updateDossierStatusUnrecognizedDocumentsCount(): void {
        const dossierStatus = this.getCachedDossierStatus();

        if (!dossierStatus) {
            return;
        }

        const unrecognizedDocumentGroup = this.getCachedRequiredDocuments()?.categories?.find((category) =>
            DocumentUtils.isNotCategorizedDocumentCategory(category)
        )?.groups?.[0];
        const unrecognizedDocumentsCount = unrecognizedDocumentGroup?.assignedDocuments?.length || 0;

        if (unrecognizedDocumentsCount !== dossierStatus.unrecognizedDocumentsCount) {
            this.setDossierStatus({ ...dossierStatus, unrecognizedDocumentsCount });
        }
    }

    public getUniqueDocumentGroupId(groupType: string, lenderId: number): string {
        return `groupType-${groupType}-lenderId-${lenderId || DefaultEmptyLenderId}`;
    }

    public removeDocumentFromInboxQueue(id: string): void {
        const currentQueue = this.inboxQueueSubject.getValue();
        const updatedQueue = currentQueue.filter((item) => item.id !== id);

        if (updatedQueue.length !== currentQueue.length) {
            this.inboxQueueSubject.next(updatedQueue);
        }
    }

    private addDocumentToQueue(document: IInboxQueueItem, shouldBeFirst = false): void {
        const currentQueue = this.inboxQueueSubject.getValue();
        const updatedQueue = shouldBeFirst ? [document, ...currentQueue] : [...currentQueue, document];

        this.inboxQueueSubject.next(updatedQueue);
    }

    /**
     * Starts listening globally for OS file drags (files dragged in from the user's file system).
     * Ref-counted: each caller must pair `startOsDirectoryFileDragListener()` with `stopOsDirectoryFileDragListener()`.
     * The underlying `window` listeners are attached on the first start and removed on the last stop.
     *
     * Uses a `dragover` heartbeat because:
     *  - `dragover` fires continuously (~every 50-350ms) as long as the file is over the window,
     *    including on the very first frame after entering - so a separate `dragenter` is redundant.
     *  - `dragleave` on window is unreliable (fires on iframe crossings, doesn't fire on ESC/cancel).
     *
     * Runs outside the Angular zone to avoid triggering change detection on every dragover tick.
     */
    public startOsDirectoryFileDragListener(): void {
        this.osDirectoryFileDragListenerRefCount++;
        if (this.osDirectoryFileDragListenerRefCount > 1) {
            return;
        }

        const onDragOver = (event: DragEvent): void => {
            if (!isOsDirectoryFileDrag(event)) {
                return;
            }
            this.signalIsDocumentDragInProgress({ inProgress: true, type: 'os-directory' });

            this.osDirectoryFileDragOverTimeout = () =>
                setTimeout(() => {
                    this.signalIsDocumentDragInProgress({ inProgress: false });
                }, this.OS_DIRECTORY_FILE_DRAG_IDLE_MS);
        };

        const onDrop = (event: DragEvent): void => {
            if (!isOsDirectoryFileDrag(event)) {
                return;
            }
            clearTimeout(this.osDirectoryFileDragOverTimeout);
            this.signalIsDocumentDragInProgress({ inProgress: false });
        };

        this.ngZone.runOutsideAngular(() => {
            const window = this.document.defaultView;
            window.addEventListener('dragover', onDragOver, true);
            window.addEventListener('drop', onDrop, true);

            this.osDirectoryFileDragCleanup = () => {
                window.removeEventListener('dragover', onDragOver, true);
                window.removeEventListener('drop', onDrop, true);
                clearTimeout(this.osDirectoryFileDragOverTimeout);
                this.signalIsDocumentDragInProgress({ inProgress: false });
            };
        });
    }

    /**
     * Decrements the ref count and, if it reaches 0, removes the global `window` listeners.
     */
    public stopOsDirectoryFileDragListener(): void {
        if (this.osDirectoryFileDragListenerRefCount === 0) {
            return;
        }

        this.osDirectoryFileDragListenerRefCount--;
        if (this.osDirectoryFileDragListenerRefCount > 0) {
            return;
        }

        this.osDirectoryFileDragCleanup?.();
        this.osDirectoryFileDragCleanup = null;
    }

    private async handleBlobErrorResponse(response: HttpErrorResponse): Promise<SafeAny> {
        const newResponse: SafeAny = { ...response };
        if (response?.error instanceof Blob) {
            const errorObjectStringified = await response.error.text();
            if (errorObjectStringified) {
                newResponse.error = JSON.parse(errorObjectStringified);
            }
        }
        return new Promise((resolve, reject) => {
            reject(newResponse as Error);
        });
    }

    private addHypoDossierDocumentUploadInProgress(documentId: string): void {
        this.hypoDossierDocumentUploadsInProgressSubject.next([
            ...(this.getHypoDossierDocumentUploadsInProgress() || []),
            { documentId }
        ]);
    }

    private removeHypoDossierDocumentUploadInProgress(documentId: string): void {
        const documentUploadsInProgressClone: IDocumentUploadInProgress[] = structuredClone(
            this.getHypoDossierDocumentUploadsInProgress()
        );
        const index = documentUploadsInProgressClone.findIndex(
            (document: IDocumentUploadInProgress) => document.documentId === documentId
        );

        if (documentUploadsInProgressClone.removeAt(index)) {
            this.hypoDossierDocumentUploadsInProgressSubject.next(documentUploadsInProgressClone);
        }
    }

    private adjustPendingRequiredDocumentsRequestsCountBy(count: number) {
        this.getRequiredDocsRequestCount += count;
        if (this.getRequiredDocsRequestCount < 0) {
            this.getRequiredDocsRequestCount = 0;
        }

        this.pendingRequiredDocumentsRequestsCountSubject.next(this.getRequiredDocsRequestCount);
    }

    private findDocument(
        dataSource: IRequiredDocuments,
        documentId: number | string,
        documentIdKey: TDocumentIdKey = DocumentIdKey.DocumentId
    ): {
        document: IDocument;
        categoryIndex: number;
        groupIndex: number;
    } {
        for (let categoryIndex = 0; categoryIndex < dataSource.categories.length; categoryIndex++) {
            const category = dataSource.categories[categoryIndex];

            for (let groupIndex = 0; groupIndex < category.groups.length; groupIndex++) {
                const group = category.groups[groupIndex];
                const documentIndex = group.assignedDocuments.findIndex((doc) => doc[documentIdKey] === documentId);

                if (documentIndex > -1) {
                    return {
                        document: group.assignedDocuments[documentIndex],
                        categoryIndex,
                        groupIndex
                    };
                }
            }
        }

        return { document: null, categoryIndex: -1, groupIndex: -1 };
    }

    private getUpdatedRequiredDocuments(
        dataSource: IRequiredDocuments,
        categoryIndex: number,
        groupIndex: number,
        assignedDocumentsCalculationFn: (documents: IDocument[]) => IDocument[]
    ): IRequiredDocuments {
        return {
            ...dataSource,
            categories: dataSource.categories.map((category, catIdx) =>
                catIdx === categoryIndex
                    ? {
                          ...category,
                          groups: category.groups.map((group, grpIdx) =>
                              grpIdx === groupIndex
                                  ? {
                                        ...group,
                                        assignedDocuments: assignedDocumentsCalculationFn(group.assignedDocuments)
                                    }
                                  : group
                          )
                      }
                    : category
            )
        };
    }
}
